import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import api from "../../services/api";
import "./DnsPropagationCheck.css";
import {
  DnsPropagationCheckNowButton,
  DnsPropagationSummary,
  DnsRecordStatusGrid,
  DnsWaitRecommendation,
  DnsReadyToSendBanner,
  DnsLastCheckedFooter,
} from "../dnsPropagationShared";

function formatCachedPropagation(data) {
  return {
    domain: data.domain,
    spfStatus: data.spfStatus || false,
    dkimStatus: data.dkimStatus || false,
    dmarcStatus: data.dmarcStatus || false,
    bimiStatus: data.bimiStatus || false,
    readyToSend: data.readyToSend || false,
    propagationScore: data.propagationScore || 0,
    recommendation: data.recommendation || {
      status: "NOT_READY",
      message: "Status unknown",
      canSendEmails: false,
      waitTime: 0,
    },
  };
}

function formatReadinessPayload(data) {
  return {
    domain: data.domain,
    spfStatus: data.records?.spf?.isPropagated || false,
    dkimStatus: data.records?.dkim?.isPropagated || false,
    dmarcStatus: data.records?.dmarc?.isPropagated || false,
    bimiStatus: data.records?.bimi?.isPropagated || false,
    readyToSend: data.readyToSend || false,
    propagationScore:
      data.overallPropagationScore || data.propagationScore || 0,
    recommendation: {
      status: data.overallStatus || data.summary?.status || "NOT_READY",
      message: data.summary?.message || data.message || "Status unknown",
      canSendEmails: data.readyToSend || false,
      waitTime: data.recommendedWaitTime || 0,
      ...(data.recommendation?.nextCheckRecommended
        ? { nextCheckRecommended: data.recommendation.nextCheckRecommended }
        : {}),
    },
  };
}

const DnsPropagationCheck = ({
  domain,
  onStatusChange,
  isCollapsible = false,
  isExpanded = false,
  onToggleExpanded,
}) => {
  const [propagationStatus, setPropagationStatus] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const initialLoadRef = useRef(true);
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  const runPropagationCheck = useCallback(async () => {
    setIsChecking(true);
    try {
      const response = await api.axios.post("/api/domains/check-readiness", {
        domain,
      });

      const formattedData = formatReadinessPayload(response.data);

      setPropagationStatus(formattedData);
      setLastCheckTime(new Date());
      setInitialLoad(false);
      initialLoadRef.current = false;

      onStatusChangeRef.current?.(formattedData);
    } catch (error) {
      console.error("Error checking propagation:", error);

      const errorStatus = {
        domain,
        spfStatus: false,
        dkimStatus: false,
        dmarcStatus: false,
        bimiStatus: false,
        readyToSend: false,
        propagationScore: 0,
        recommendation: {
          status: "ERROR",
          message: "Failed to check DNS propagation. Please try again.",
          canSendEmails: false,
          waitTime: 5,
        },
      };

      setPropagationStatus(errorStatus);
      setInitialLoad(false);
      initialLoadRef.current = false;
    } finally {
      setIsChecking(false);
    }
  }, [domain]);

  const fetchPropagationStatus = useCallback(async () => {
    try {
      const response = await api.axios.get(
        `/api/domains/${domain}/propagation`,
      );

      if (response.data && response.data.lastPropagationCheck) {
        const formattedData = formatCachedPropagation(response.data);

        setPropagationStatus(formattedData);
        setLastCheckTime(new Date(response.data.lastPropagationCheck));
        setInitialLoad(false);
        initialLoadRef.current = false;

        onStatusChangeRef.current?.(formattedData);
      } else {
        if (initialLoadRef.current) {
          await runPropagationCheck();
        }
        initialLoadRef.current = false;
        setInitialLoad(false);
      }
    } catch (error) {
      console.error("Error fetching propagation status:", error);
      if (initialLoadRef.current) {
        await runPropagationCheck();
      }
      initialLoadRef.current = false;
      setInitialLoad(false);
    }
  }, [domain, runPropagationCheck]);

  useEffect(() => {
    initialLoadRef.current = true;
    if (domain) {
      fetchPropagationStatus();
    }
  }, [domain, fetchPropagationStatus]);

  useEffect(() => {
    let interval = null;
    if (autoRefresh && domain) {
      interval = setInterval(() => {
        fetchPropagationStatus();
      }, 120000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, domain, fetchPropagationStatus]);

  const accordionDetailsInner = propagationStatus && (
    <div className="dns-propagation-check">
      <div className="propagation-controls">
        <label className="auto-refresh-toggle">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh
        </label>
        <DnsPropagationCheckNowButton
          onClick={runPropagationCheck}
          isChecking={isChecking}
        />
      </div>

      <DnsPropagationSummary
        readyToSend={propagationStatus.readyToSend}
        score={propagationStatus.propagationScore}
        recommendation={propagationStatus.recommendation}
      />

      <DnsRecordStatusGrid
        spfStatus={propagationStatus.spfStatus}
        dkimStatus={propagationStatus.dkimStatus}
        dmarcStatus={propagationStatus.dmarcStatus}
        bimiStatus={propagationStatus.bimiStatus}
      />

      <DnsReadyToSendBanner />

      <DnsLastCheckedFooter
        lastCheckTime={lastCheckTime}
        timeSinceLastCheck={propagationStatus?.timeSinceLastCheck}
      />
    </div>
  );

  if (
    isCollapsible &&
    propagationStatus &&
    propagationStatus.propagationScore >= 100
  ) {
    return (
      <Accordion expanded={isExpanded} onChange={onToggleExpanded}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: "#e8f5e8",
            border: "1px solid #4caf50",
            borderRadius: 1,
            "&:hover": {
              backgroundColor: "#f1f8e9",
            },
          }}
        >
          <Box display="flex" alignItems="center" gap={2} width="100%">
            <CheckCircleIcon sx={{ color: "#4caf50" }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "#2e7d32" }}
            >
              DNS Propagation Status
            </Typography>
            <Box
              sx={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Chip
                label={`${propagationStatus.propagationScore}%`}
                sx={{
                  backgroundColor: "#4caf50",
                  color: "white",
                  fontWeight: "bold",
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: "#2e7d32", fontWeight: "bold" }}
              >
                ✅ Ready to Send!
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>{accordionDetailsInner}</AccordionDetails>
      </Accordion>
    );
  }

  if (initialLoad && !propagationStatus) {
    return (
      <div className="dns-propagation-check loading">
        <div className="propagation-header">
          <h4>DNS Propagation Status</h4>
          <DnsPropagationCheckNowButton
            onClick={runPropagationCheck}
            isChecking={isChecking}
          />
        </div>
        <p>
          Click &quot;Check Now&quot; to verify DNS propagation status across 8
          worldwide DNS servers.
          <br />
          <small style={{ color: "#666" }}>
            This check ensures your domain is ready for email sending and helps
            prevent delivery issues.
          </small>
        </p>
      </div>
    );
  }

  return (
    <div className="dns-propagation-check">
      <div className="propagation-header">
        <h4>DNS Propagation Status</h4>
        <div className="propagation-controls">
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <DnsPropagationCheckNowButton
            onClick={runPropagationCheck}
            isChecking={isChecking}
          />
        </div>
      </div>

      {propagationStatus && (
        <>
          <DnsPropagationSummary
            readyToSend={propagationStatus.readyToSend}
            score={propagationStatus.propagationScore}
            recommendation={propagationStatus.recommendation}
          />

          <DnsRecordStatusGrid
            spfStatus={propagationStatus.spfStatus}
            dkimStatus={propagationStatus.dkimStatus}
            dmarcStatus={propagationStatus.dmarcStatus}
            bimiStatus={propagationStatus.bimiStatus}
          />

          {!propagationStatus.readyToSend &&
            propagationStatus.recommendation?.waitTime > 0 && (
              <DnsWaitRecommendation
                waitMinutes={propagationStatus.recommendation.waitTime}
                nextCheckRecommended={
                  propagationStatus.recommendation.nextCheckRecommended
                }
              />
            )}

          {propagationStatus.readyToSend && <DnsReadyToSendBanner />}
        </>
      )}

      <DnsLastCheckedFooter
        lastCheckTime={lastCheckTime}
        timeSinceLastCheck={propagationStatus?.timeSinceLastCheck}
      />
    </div>
  );
};

export default DnsPropagationCheck;
